<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import Select from 'primevue/select'
import D3DonutChart from '@/components/charts/D3DonutChart.vue'
import D3BarChart from '@/components/charts/D3BarChart.vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { regionNameFromISO2, normalizeISO2 } from '@/utils/country'

const { t, activeLang } = useI18n()
const router = useRouter()
const { fmtDate } = useFormatDate()

const props = defineProps({
  // Current table filter state, keyed by dimension — marks the active slices
  activeFilters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['filter'])

const loading = ref(true)
const error   = ref('')
const stats   = ref(null)
const days    = ref(30)

const ACT_COLORS = { active: '#22c55e', inactive: '#64748b' }

const daysOptions = computed(() => [
  { label: t('hackwatch.groups.statsDays7'),   value: 7 },
  { label: t('hackwatch.groups.statsDays30'),  value: 30 },
  { label: t('hackwatch.groups.statsDays90'),  value: 90 },
  { label: t('hackwatch.groups.statsDays365'), value: 365 },
])

async function fetchStats() {
  loading.value = true
  error.value   = ''
  try {
    const { data: res } = await api.getGroupsStats({ days: days.value })
    stats.value = res?.data ?? null
  } catch {
    error.value = t('hackwatch.groups.statsLoadFailed')
  } finally {
    loading.value = false
  }
}

// Changing the window refetches; an active activity filter follows the new window
watch(days, () => {
  fetchStats()
  if (props.activeFilters?.activity) {
    emit('filter', { dimension: 'activity', value: props.activeFilters.activity, days: days.value })
  }
})

const activityData = computed(() => {
  const a = stats.value?.activity ?? {}
  return [
    { label: t('hackwatch.groups.statsActive'),   value: a.active   ?? 0, color: ACT_COLORS.active,   raw: 'active' },
    { label: t('hackwatch.groups.statsInactive'), value: a.inactive ?? 0, color: ACT_COLORS.inactive, raw: 'inactive' },
  ]
})

const countryLabel = (code) => {
  const c = normalizeISO2(code) || code
  return regionNameFromISO2(c, [activeLang.value]) || c
}

const countriesData = computed(() => (stats.value?.countries ?? []).map(r => ({
  label: r.name ? countryLabel(r.name) : t('hackwatch.groups.statsUnknown'),
  value: r.count,
  raw:   r.name ?? null,
})))

const ttpsData = computed(() => (stats.value?.ttps ?? []).map(r => ({
  label: r.mitre_id ? `${r.mitre_id} · ${r.name}` : r.name,
  value: r.count,
  raw:   r.id,
  name:  r.name,
})))

const toolsData = computed(() => (stats.value?.tools ?? []).map(r => ({
  label: r.name,
  value: r.count,
  raw:   r.id,
  name:  r.name,
})))

const yearsData = computed(() => (stats.value?.years ?? []).map(r => ({
  label: String(r.year),
  value: r.count,
  raw:   r.year,
})))

const recentGroups = computed(() => stats.value?.recent ?? [])

function onSliceClick(dimension, item) {
  if (item.isOther || item.raw == null) return
  const payload = { dimension, value: item.raw }
  if (dimension === 'activity') payload.days = days.value
  if (dimension === 'ttp' || dimension === 'tool') payload.label = item.name ?? item.label
  emit('filter', payload)
}

function goGroup(g) {
  router.push(`/threat-intel/actors/${g.id}`)
}

onMounted(fetchStats)
defineExpose({ refresh: fetchStats })
</script>

<template>
  <div class="actors-stats-panel">
    <!-- ── Aktivität (mit Zeitfenster) ─────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-chart-line hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsActivity') }}</span>
        <Select
          v-model="days"
          :options="daysOptions"
          option-label="label"
          option-value="value"
          size="small"
          class="asp-days-select"
        />
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="activityData"
          :selected="props.activeFilters?.activity ?? null"
          @slice-click="item => onSliceClick('activity', item)"
        />
      </div>
    </div>

    <!-- ── Neueste Gruppen (Liste) ─────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-plus-circle hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsRecent') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <ul class="asp-recent-list">
          <li
            v-for="g in recentGroups"
            :key="g.id"
            class="asp-recent-row"
            @click="goGroup(g)"
          >
            <img
              v-if="g.country"
              :src="`/assets/flags/svg/${String(g.country).toLowerCase()}.svg`"
              class="asp-recent-flag"
              :alt="g.country"
              @error="e => e.target.style.visibility = 'hidden'"
            />
            <span v-else class="asp-recent-flag asp-recent-flag--empty" />
            <span class="asp-recent-name" :title="g.name">{{ g.name }}</span>
            <span class="asp-recent-date">{{ fmtDate(g.created_at) }}</span>
          </li>
          <li v-if="!recentGroups.length" class="asp-empty">—</li>
        </ul>
      </div>
    </div>

    <!-- ── Länder ──────────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-flag hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsCountries') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="countriesData"
          :other-label="t('hackwatch.groups.statsOther')"
          :selected="props.activeFilters?.country ?? null"
          @slice-click="item => onSliceClick('country', item)"
        />
      </div>
    </div>

    <!-- ── Top TTPs ────────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-table hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsTtps') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="ttpsData"
          :other-label="t('hackwatch.groups.statsOther')"
          :selected="props.activeFilters?.ttp ?? null"
          @slice-click="item => onSliceClick('ttp', item)"
        />
      </div>
    </div>

    <!-- ── Top Tools ───────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-wrench hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsTools') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="toolsData"
          :other-label="t('hackwatch.groups.statsOther')"
          :selected="props.activeFilters?.tool ?? null"
          @slice-click="item => onSliceClick('tool', item)"
        />
      </div>
    </div>

    <!-- ── First Seen pro Jahr ─────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-calendar hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.statsYears') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3BarChart
          :data="yearsData"
          :selected="props.activeFilters?.year ?? null"
          @bar-click="item => onSliceClick('year', item)"
        />
      </div>
    </div>

    <div v-if="!loading && error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.actors-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.asp-days-select {
  font-size: .72rem;
}
.asp-recent-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.asp-recent-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background .12s;
}
.asp-recent-row:hover {
  background: color-mix(in srgb, var(--p-primary-color) 6%, transparent);
}
.asp-recent-flag {
  width: 18px;
  height: 12px;
  object-fit: cover;
  border-radius: 1px;
  flex-shrink: 0;
}
.asp-recent-flag--empty { visibility: hidden; }
.asp-recent-name {
  flex: 1;
  min-width: 0;
  font-size: .75rem;
  font-weight: 600;
  color: var(--p-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.asp-recent-date {
  font-size: .65rem;
  color: var(--p-text-color-secondary);
  flex-shrink: 0;
}
.asp-empty {
  padding: 6px 4px;
  font-size: .7rem;
  color: var(--p-text-color-secondary);
}
</style>
